import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Eye, ThumbsUp, ThumbsDown, PlayCircle } from "lucide-react";
import ArticleRenderer from "@/components/help/ArticleRenderer";
import { api } from "@/lib/api";

const REASONS = [
  { v: "unclear", l: "Information was unclear" },
  { v: "steps_failed", l: "Steps did not work" },
  { v: "outdated", l: "Information was outdated" },
  { v: "different_issue", l: "My issue was different" },
  { v: "other", l: "Other" },
];

export default function HelpArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [voted, setVoted] = useState(null);
  const [showReasons, setShowReasons] = useState(false);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setData(null); setMissing(false); setVoted(null); setShowReasons(false);
    api.get(`/help/articles/${slug}`)
      .then((r) => { setData(r.data); if (r.data.my_feedback) setVoted(r.data.my_feedback.helpful); })
      .catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const sendFeedback = async (helpful, r = "", c = "") => {
    try {
      await api.post(`/help/articles/${slug}/feedback`, { helpful, reason: r, comment: c });
      setVoted(helpful);
      if (helpful) { setShowReasons(false); toast.success("Thanks for the feedback!"); }
    } catch { toast.error("Couldn't save feedback"); }
  };

  if (missing) {
    return (
      <div className="text-center py-16" data-testid="help-article-missing">
        <p className="text-sm text-ink-muted mb-4">This article doesn't exist or isn't published.</p>
        <Link to="/dashboard/help" className="text-brand-amber text-sm hover:underline">← Back to Help Center</Link>
      </div>
    );
  }
  if (!data) return <div className="font-mono text-xs text-brand-amber py-10">LOADING…</div>;
  const a = data.article;

  return (
    <div className="max-w-3xl" data-testid="help-article-page">
      <Link to="/dashboard/help" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Help Center
      </Link>
      <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// {a.category}</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink-primary mb-2" data-testid="help-article-title">{a.title}</h1>
      <p className="text-sm text-ink-secondary mb-3">{a.description}</p>
      <div className="flex flex-wrap items-center gap-4 font-mono text-[9px] uppercase tracking-wider text-ink-muted border-b border-line pb-4 mb-6">
        <span className="inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{a.read_minutes} min read</span>
        <span className="inline-flex items-center gap-1"><Eye className="h-2.5 w-2.5" />{a.views} views</span>
        <span>Updated {new Date(a.updated_at).toLocaleDateString()}</span>
        <span>By {a.author}</span>
      </div>

      {a.featured_image && <img src={a.featured_image} alt={a.title} className="rounded-xl border border-line mb-6 w-full" />}
      <ArticleRenderer content={a.content} />
      {a.video_url && (
        <a href={a.video_url} target="_blank" rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-secondary hover:text-brand-amber transition">
          <PlayCircle className="h-4 w-4" /> Watch the video walkthrough
        </a>
      )}

      {/* Feedback */}
      <div className="mt-10 rounded-xl border border-line bg-bg-surface p-5" data-testid="help-article-feedback">
        <div className="text-sm font-semibold text-ink-primary mb-3">Was this article helpful?</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => sendFeedback(true)} data-testid="help-feedback-yes"
            className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-xs transition ${
              voted === true ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300" : "border-line bg-bg-elevated text-ink-secondary hover:text-emerald-300"}`}>
            <ThumbsUp className="h-3.5 w-3.5" /> Yes
          </button>
          <button type="button" onClick={() => { setVoted(false); setShowReasons(true); }} data-testid="help-feedback-no"
            className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-xs transition ${
              voted === false ? "border-rose-500/60 bg-rose-500/15 text-rose-300" : "border-line bg-bg-elevated text-ink-secondary hover:text-rose-300"}`}>
            <ThumbsDown className="h-3.5 w-3.5" /> No
          </button>
        </div>
        {showReasons && (
          <div className="mt-4 space-y-3" data-testid="help-feedback-reasons">
            <div className="text-xs text-ink-secondary">Sorry about that — what went wrong?</div>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button key={r.v} type="button" onClick={() => setReason(r.v)}
                  data-testid={`help-feedback-reason-${r.v}`}
                  className={`rounded-full border px-3 py-1.5 text-[11px] transition ${
                    reason === r.v ? "border-brand-amber/70 bg-brand-amber/15 text-brand-amber" : "border-line bg-bg-elevated text-ink-secondary"}`}>
                  {r.l}
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more (optional)…" data-testid="help-feedback-comment"
              className="w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 min-h-[70px]" />
            <button type="button" disabled={!reason} data-testid="help-feedback-submit"
              onClick={() => { sendFeedback(false, reason, comment); setShowReasons(false); toast.success("Feedback sent — thank you"); }}
              className="rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
              Send feedback
            </button>
          </div>
        )}
      </div>

      {/* Related */}
      {data.related?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">// Related articles</h2>
          <div className="space-y-2" data-testid="help-article-related">
            {data.related.map((r) => (
              <Link key={r.slug} to={`/dashboard/help/article/${r.slug}`}
                className="block rounded-lg border border-line bg-bg-surface px-4 py-3 hover:border-brand-amber/40 transition">
                <div className="text-sm text-ink-primary">{r.title}</div>
                <div className="text-[11px] text-ink-muted">{r.category} · {r.read_minutes} min read</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
